import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Bell, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { get } from "@/lib/api";
import { cn } from "@/lib/utils";
import { EmptyState, PageHeader, SkeletonRows } from "@/components/app/Common";
import { NotificationItem, useNotifActions } from "@/components/app/NotificationsBell";

const TYPES = [["", "All"], ["link_opened", "Opened"], ["link_started", "Started"], ["link_completed", "Completed"], ["system", "Other"]];

export default function NotificationsPage() {
  const nav = useNavigate();
  const [type, setType] = useState("");
  const [unread, setUnread] = useState(false);
  const { read, readAll } = useNotifActions();
  const { data, isLoading } = useQuery({ queryKey: ["notifications", "page", type, unread], queryFn: () => get("/notifications", { type: type || undefined, unread: unread || undefined, limit: 200 }) });
  const open = async (n) => { await read(n); if (n.link) nav(n.link); };
  return (
    <div className="space-y-6" data-tour="pt-notifications-page" data-testid="notifications-page">
      <PageHeader title="Notifications" subtitle="Alerts when candidates open, start or finish their links, plus other updates."
        actions={<Button variant="outline" onClick={readAll} disabled={!data?.unread} data-tour="pt-notifications-page-mark-all" data-testid="notifications-page-mark-all"><CheckCheck size={16} aria-hidden="true" /> Mark all read</Button>} />
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-[5px] border border-line bg-white p-0.5" role="tablist" aria-label="Filter by type">
          {TYPES.map(([k, l]) => (
            <button key={k} role="tab" aria-selected={type === k} onClick={() => setType(k)} data-testid={`notif-filter-${k || "all"}`}
              className={cn("rounded-md px-3 py-1 text-sm transition-colors duration-150", type === k ? "bg-brand-tint font-medium text-brand" : "text-ink-secondary hover:text-ink")}>{l}</button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-ink-secondary"><input type="checkbox" checked={unread} onChange={(e) => setUnread(e.target.checked)} className="accent-[var(--brand)]" data-tour="pt-notif-filter-unread" data-testid="notif-filter-unread" /> Unread only</label>
      </div>
      {isLoading ? <SkeletonRows rows={6} /> : !data.items.length ? <EmptyState icon={Bell} title="Nothing here yet" text="You'll see an alert the first time a candidate opens one of their links." testId="notifications-page-empty" /> : (
        <div className="panel divide-y divide-line p-2">{data.items.map((n) => <NotificationItem key={n.id} n={n} onOpen={open} testId={`notifications-page-item-${n.id}`} />)}</div>
      )}
    </div>
  );
}
